import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, HelpCircle, Plus, Send, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  SegmentedControl,
  Textarea,
  useToast,
} from '@/components/ui'

// ─── Types ───────────────────────────────────────────────────────────────────

interface MonitorAnswer {
  id: string
  entryId: string
  text: string
  createdByUserId: string | null
  createdAt: string
}

interface MonitorEntry {
  id: string
  type: 'TODO' | 'OPEN_QUESTION'
  text: string
  status: string
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
  answers?: MonitorAnswer[]
}

type ActiveTab = 'todos' | 'questions'

const STORAGE_TAB_KEY = 'paperclip-monitor-entries-tab'

// ─── API ──────────────────────────────────────────────────────────────────────

async function fetchEntries(): Promise<MonitorEntry[]> {
  const { data } = await apiClient.get<MonitorEntry[]>('/paperclip/monitor-entries')
  return data
}

async function apiCreateTodo(text: string): Promise<MonitorEntry> {
  const { data } = await apiClient.post<MonitorEntry>('/paperclip/monitor-entries', { type: 'TODO', text })
  return data
}

async function apiCreateQuestion(text: string): Promise<MonitorEntry> {
  const { data } = await apiClient.post<MonitorEntry>('/paperclip/monitor-entries', { type: 'OPEN_QUESTION', text })
  return data
}

async function apiSetTodoStatus(id: string, status: 'open' | 'done'): Promise<MonitorEntry> {
  const { data } = await apiClient.patch<MonitorEntry>(`/paperclip/monitor-entries/${id}/status`, { status })
  return data
}

async function apiDeleteTodo(id: string): Promise<void> {
  await apiClient.delete(`/paperclip/monitor-entries/${id}`)
}

async function apiAddAnswer(entryId: string, text: string): Promise<MonitorAnswer> {
  const { data } = await apiClient.post<MonitorAnswer>(`/paperclip/monitor-entries/${entryId}/answers`, { text })
  return data
}

async function apiResolveQuestion(id: string): Promise<MonitorEntry> {
  const { data } = await apiClient.patch<MonitorEntry>(`/paperclip/monitor-entries/${id}/resolve`, {})
  return data
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function extractErrorMsg(err: unknown, fallback: string): string {
  const axiosMsg = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
    ?? (err as { response?: { data?: { error?: string } } })?.response?.data?.error
  if (axiosMsg) return axiosMsg
  if (err instanceof Error) return err.message
  return fallback
}

// ─── TodosList ────────────────────────────────────────────────────────────────

function TodosList({ entries }: { entries: MonitorEntry[] }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [newText, setNewText] = useState('')
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)

  const todos = entries.filter(e => e.type === 'TODO')

  const createMutation = useMutation({
    mutationFn: apiCreateTodo,
    onSuccess: (created) => {
      setNewText('')
      setCreateError(null)
      queryClient.setQueryData<MonitorEntry[]>(['paperclip-monitor-entries'], prev =>
        prev ? [...prev, created] : [created],
      )
    },
    onError: (err: unknown) => {
      const msg = extractErrorMsg(err, 'Fehler beim Erstellen')
      setCreateError(msg)
      toast({ title: msg, tone: 'danger' })
    },
  })

  const doneMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => apiSetTodoStatus(id, 'done'),
    onSuccess: (updated) => {
      queryClient.setQueryData<MonitorEntry[]>(['paperclip-monitor-entries'], prev =>
        prev ? prev.filter(e => e.id !== updated.id) : [],
      )
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler'
      toast({ title: msg, tone: 'danger' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDeleteTodo(id),
    onSuccess: (_, id) => {
      setDeleteTargetId(null)
      queryClient.setQueryData<MonitorEntry[]>(['paperclip-monitor-entries'], prev =>
        prev ? prev.filter(e => e.id !== id) : [],
      )
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler beim Löschen'
      toast({ title: msg, tone: 'danger' })
    },
  })

  function handleCreate() {
    if (!newText.trim() || createMutation.isPending) return
    createMutation.mutate(newText.trim())
  }

  const deleteTarget = todos.find(t => t.id === deleteTargetId)

  return (
    <div className="flex flex-col gap-4">
      {/* Input */}
      <div className="flex gap-2">
        <Input
          value={newText}
          onChange={e => { setNewText(e.target.value); if (createError) setCreateError(null) }}
          placeholder="Neues Todo eingeben…"
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          className="flex-1"
        />
        <Button
          variant="primary"
          size="sm"
          onClick={handleCreate}
          disabled={!newText.trim() || createMutation.isPending}
          title={!newText.trim() ? 'Bitte zuerst Text eingeben' : 'Todo erstellen'}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Persistent error */}
      {createError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <strong>Fehler:</strong> {createError}
        </div>
      )}

      {/* List */}
      {todos.length === 0 ? (
        <EmptyState
          title="Keine offenen Todos"
          description="Trage oben ein neues Todo ein."
          icon={<CheckCircle2 className="h-8 w-8 text-muted-foreground" />}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {todos.map(todo => (
            <Card key={todo.id} className="shadow-none">
              <Card.Body className="flex items-start justify-between gap-3 py-3">
                <p className="flex-1 text-sm leading-snug">{todo.text}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => doneMutation.mutate({ id: todo.id })}
                    disabled={doneMutation.isPending && doneMutation.variables?.id === todo.id}
                    title="Als erledigt markieren"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    Done
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setDeleteTargetId(todo.id)}
                    title="Todo löschen"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteTargetId !== null}
        title="Todo löschen"
        message={
          <p className="text-sm text-muted-foreground">
            Soll das Todo <strong>&ldquo;{deleteTarget?.text}&rdquo;</strong> endgültig gelöscht werden?
          </p>
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTargetId && deleteMutation.mutate(deleteTargetId)}
        onCancel={() => setDeleteTargetId(null)}
      />
    </div>
  )
}

// ─── QuestionCard ─────────────────────────────────────────────────────────────

function QuestionCard({ entry }: { entry: MonitorEntry }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [answerText, setAnswerText] = useState('')
  const [answerError, setAnswerError] = useState<string | null>(null)

  const addAnswerMutation = useMutation({
    mutationFn: (text: string) => apiAddAnswer(entry.id, text),
    onSuccess: (answer) => {
      setAnswerText('')
      setAnswerError(null)
      queryClient.setQueryData<MonitorEntry[]>(['paperclip-monitor-entries'], prev =>
        prev
          ? prev.map(e =>
              e.id === entry.id
                ? { ...e, answers: [...(e.answers ?? []), answer] }
                : e,
            )
          : [],
      )
    },
    onError: (err: unknown) => {
      const msg = extractErrorMsg(err, 'Fehler beim Speichern')
      setAnswerError(msg)
      toast({ title: msg, tone: 'danger' })
    },
  })

  const resolveMutation = useMutation({
    mutationFn: () => apiResolveQuestion(entry.id),
    onSuccess: (updated) => {
      queryClient.setQueryData<MonitorEntry[]>(['paperclip-monitor-entries'], prev =>
        prev ? prev.filter(e => e.id !== updated.id) : [],
      )
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler'
      toast({ title: msg, tone: 'danger' })
    },
  })

  return (
    <Card className="shadow-none">
      <Card.Body className="flex flex-col gap-3">
        {/* Question text */}
        <div className="flex items-start justify-between gap-3">
          <p className="flex-1 text-sm font-medium leading-snug">{entry.text}</p>
          <Button
            variant="secondary"
            size="xs"
            onClick={() => resolveMutation.mutate()}
            disabled={resolveMutation.isPending}
            className="shrink-0"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            Fertig
          </Button>
        </div>

        {/* Answer history */}
        {entry.answers && entry.answers.length > 0 && (
          <div className="flex flex-col gap-2 border-t pt-2">
            {entry.answers.map(answer => (
              <div key={answer.id} className="rounded-md bg-muted/40 px-3 py-2 text-sm">
                <p className="whitespace-pre-wrap leading-snug">{answer.text}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(answer.createdAt).toLocaleString('de-DE')}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* New answer input */}
        <div className="flex flex-col gap-2 border-t pt-2">
          <Textarea
            value={answerText}
            onChange={e => { setAnswerText(e.target.value); if (answerError) setAnswerError(null) }}
            placeholder="Antwort schreiben…"
            rows={2}
          />
          {answerError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <strong>Fehler:</strong> {answerError}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="xs"
              onClick={() => addAnswerMutation.mutate(answerText.trim())}
              disabled={!answerText.trim() || addAnswerMutation.isPending}
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              Antwort speichern
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── QuestionsList ────────────────────────────────────────────────────────────

function QuestionsList({ entries }: { entries: MonitorEntry[] }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [newText, setNewText] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  const questions = entries.filter(e => e.type === 'OPEN_QUESTION')

  const createMutation = useMutation({
    mutationFn: apiCreateQuestion,
    onSuccess: (created) => {
      setNewText('')
      setCreateError(null)
      queryClient.setQueryData<MonitorEntry[]>(['paperclip-monitor-entries'], prev =>
        prev ? [...prev, { ...created, answers: [] }] : [{ ...created, answers: [] }],
      )
    },
    onError: (err: unknown) => {
      const msg = extractErrorMsg(err, 'Fehler beim Erstellen')
      setCreateError(msg)
      toast({ title: msg, tone: 'danger' })
    },
  })

  function handleCreate() {
    if (!newText.trim() || createMutation.isPending) return
    createMutation.mutate(newText.trim())
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Input */}
      <div className="flex flex-col gap-2">
        <Textarea
          value={newText}
          onChange={e => { setNewText(e.target.value); if (createError) setCreateError(null) }}
          placeholder="Neue offene Frage eingeben…"
          rows={2}
        />
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreate}
            disabled={!newText.trim() || createMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-1" />
            Frage erstellen
          </Button>
        </div>
      </div>

      {/* Persistent error */}
      {createError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <strong>Fehler:</strong> {createError}
        </div>
      )}

      {/* List */}
      {questions.length === 0 ? (
        <EmptyState
          title="Keine offenen Fragen"
          description="Trage oben eine neue Frage ein."
          icon={<HelpCircle className="h-8 w-8 text-muted-foreground" />}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {questions.map(q => (
            <QuestionCard key={q.id} entry={q} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Panel ───────────────────────────────────────────────────────────────────

export function MonitorEntriesPanel({ compact = false }: { compact?: boolean }) {
  const [activeTab, setActiveTab] = useState<ActiveTab>(
    () => (localStorage.getItem(STORAGE_TAB_KEY) as ActiveTab | null) ?? 'todos',
  )

  function handleTabChange(tab: ActiveTab) {
    setActiveTab(tab)
    localStorage.setItem(STORAGE_TAB_KEY, tab)
  }

  const { data: entries, isLoading, isError, error } = useQuery({
    queryKey: ['paperclip-monitor-entries'],
    queryFn: fetchEntries,
    staleTime: 30_000,
  })

  const toggle = (
    <SegmentedControl<ActiveTab>
      value={activeTab}
      onValueChange={handleTabChange}
      size="sm"
      fullWidth={compact}
      options={[
        {
          value: 'todos',
          label: (
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 shrink-0" />
              Todos
              {entries && (
                <span className="text-xs opacity-70">
                  ({entries.filter(e => e.type === 'TODO').length})
                </span>
              )}
            </span>
          ),
        },
        {
          value: 'questions',
          label: (
            <span className="flex items-center gap-1">
              <HelpCircle className="h-3 w-3 shrink-0" />
              Fragen
              {entries && (
                <span className="text-xs opacity-70">
                  ({entries.filter(e => e.type === 'OPEN_QUESTION').length})
                </span>
              )}
            </span>
          ),
        },
      ]}
    />
  )

  const body = (
    <>
      {isLoading && <LoadingState label="Einträge werden geladen…" />}
      {isError && (
        <ErrorState
          title="Einträge konnten nicht geladen werden"
          details={error instanceof Error ? error.message : String(error)}
        />
      )}
      {!isLoading && !isError && entries && (
        activeTab === 'todos'
          ? <TodosList entries={entries} />
          : <QuestionsList entries={entries} />
      )}
    </>
  )

  if (compact) {
    return (
      <div className="flex flex-col gap-3">
        {toggle}
        {body}
      </div>
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Card.Title>Todos & Offene Fragen</Card.Title>
          {toggle}
        </div>
      </Card.Header>
      <Card.Body>
        {body}
      </Card.Body>
    </Card>
  )
}
